// Implements: F-UIX-019
/**
 * The ceiling of one organization as a form (UIX.8): the generations and the budget each person
 * may get per month and, optionally, the organization's total, each one empty for "no bound".
 *
 * It holds no state. Each refused field carries its own sentence (`role="alert"`, wired to the
 * input by `aria-describedby`); a refusal of the server, whose envelope names no field, is shown
 * above the button. Each money field shows the exact amount it will be saved as ("Se guardará
 * como US$ 1,000.00"), so a decimal mark read the wrong way is seen before saving. A stricter
 * ceiling is explained before it is saved, the note describing the Save button, and never
 * blocked: the server accepts it and clamps what was distributed (N3). Whether the distribution
 * really exceeds the ceiling is the server's answer (`distribution_exceeds_ceiling`): said on
 * opening for the stored ceiling, and after a save for the ceiling just stored.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { amountMicros } from "../model/ceilingForm";
import type { CeilingDraft, CeilingField, FieldError, FieldErrors } from "../model/ceilingForm";
import { costText } from "../model/money";
import styles from "./Ceilings.module.css";

/** The currency the ceilings are kept in (micro-dollars). */
const CEILING_CURRENCY = "USD";

/** Label, hint and out-of-range sentence of each field, in the order the form shows them. */
const FIELDS = [
  {
    field: "generations",
    label: "admin.ceilings.generations",
    hint: "admin.ceilings.generationsHint",
    error: "admin.ceilings.generationsError",
    inputMode: "numeric",
    money: false,
  },
  {
    field: "budget",
    label: "admin.ceilings.budget",
    hint: "admin.ceilings.budgetHint",
    error: "admin.ceilings.amountError",
    inputMode: "decimal",
    money: true,
  },
  {
    field: "total",
    label: "admin.ceilings.total",
    hint: "admin.ceilings.totalHint",
    error: "admin.ceilings.amountError",
    inputMode: "decimal",
    money: true,
  },
] as const satisfies readonly {
  readonly field: CeilingField;
  readonly label: TranslationKey;
  readonly hint: TranslationKey;
  readonly error: TranslationKey;
  readonly inputMode: "numeric" | "decimal";
  readonly money: boolean;
}[];

/** The out-of-range sentences, which take no parameter. */
type RangeErrorKey = (typeof FIELDS)[number]["error"];

/**
 * What the last save left on screen: nothing, or saved, with whether the distribution exceeds the
 * ceiling just stored (the server's answer) and whether the new ceiling was stricter.
 */
export type SaveOutcome = { readonly exceeds: boolean; readonly tighter: boolean } | null;

/** The saved line: the distribution exceeds it, fits under a stricter one, or nothing to add. */
function savedKey(saved: NonNullable<SaveOutcome>): "admin.ceilings.exceeds" | "admin.ceilings.fits" | null {
  if (saved.exceeds) return "admin.ceilings.exceeds";
  return saved.tighter ? "admin.ceilings.fits" : null;
}

export interface CeilingFormProps {
  readonly draft: CeilingDraft;
  readonly errors: FieldErrors;
  /** The draft is stricter than the stored ceiling. */
  readonly tighter: boolean;
  /** The server says the distribution exceeds the stored ceiling. */
  readonly storedExceeds: boolean;
  readonly saving: boolean;
  /** The server's refusal, as the admin reads it, or `null`. */
  readonly refusal: string | null;
  readonly saved: SaveOutcome;
  readonly onChange: (field: CeilingField, value: string) => void;
  readonly onSubmit: () => void;
}

export function CeilingForm({
  draft,
  errors,
  tighter,
  storedExceeds,
  saving,
  refusal,
  saved,
  onChange,
  onSubmit,
}: CeilingFormProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const tighterId = `${ids}-tighter`;
  const savedNote = saved === null ? null : savedKey(saved);

  function errorText(kind: FieldError | undefined, rangeKey: RangeErrorKey): string | undefined {
    if (kind === undefined) return undefined;
    return kind === "thousands" ? t("admin.ceilings.thousandsError") : t(rangeKey);
  }

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <fieldset className={styles.form}>
        <legend className={styles.legend}>{t("admin.ceilings.legend")}</legend>
        {storedExceeds && saved === null ? <p className={styles.note}>{t("admin.ceilings.exceeds")}</p> : null}
        {FIELDS.map(({ field, label, hint, error, inputMode, money }) => {
          const id = `${ids}-${field}`;
          const message = errorText(errors[field], error);
          const micros = money ? amountMicros(draft[field]) : undefined;
          const preview =
            typeof micros === "number"
              ? t("admin.ceilings.preview", { amount: costText(micros, CEILING_CURRENCY) })
              : undefined;
          const previewId = `${id}-preview`;
          const described = [describedBy(id, { hint: t(hint), error: message }), preview === undefined ? undefined : previewId]
            .filter((entry): entry is string => entry !== undefined)
            .join(" ");
          return (
            <div key={field}>
              <Field id={id} label={t(label)} hint={t(hint)} error={message}>
                <Input
                  id={id}
                  value={draft[field]}
                  inputMode={inputMode}
                  invalid={message !== undefined}
                  describedBy={described}
                  disabled={saving}
                  onChange={(value) => {
                    onChange(field, value);
                  }}
                />
              </Field>
              {preview === undefined ? null : (
                <p id={previewId} className={styles.preview}>
                  {preview}
                </p>
              )}
            </div>
          );
        })}
        {tighter ? (
          <p id={tighterId} className={styles.note}>
            {t("admin.ceilings.tighter")}
          </p>
        ) : null}
        {refusal === null ? null : (
          <p className={styles.refusal} role="alert">
            {refusal}
          </p>
        )}
        <div>
          <Button
            variant="primary"
            type="submit"
            loading={saving}
            disabled={saving}
            describedBy={tighter ? tighterId : undefined}
          >
            {t("admin.ceilings.save")}
          </Button>
        </div>
        {saved === null ? null : (
          <p className={styles.saved} role="status">
            {savedNote === null ? t("admin.ceilings.saved") : `${t("admin.ceilings.saved")} ${t(savedNote)}`}
          </p>
        )}
      </fieldset>
    </form>
  );
}
